import { Component, inject } from '@angular/core';
import {
  BookingCategoryType,
  SecondaryNavActionEvent
} from '@com-delta-idp/mnx-sitewide-header-ui';
import { filter, takeUntil } from 'rxjs/operators';
import { HeaderBaseExtendedComponent } from './header-base-extended.component';
import { DarkSiteThemeService, SecondaryNavigationService } from './services';

@Component({
  template: '',
  standalone: true
})
export class HeaderBaseComponent extends HeaderBaseExtendedComponent {
  readonly darkSiteThemeService = inject(DarkSiteThemeService);
  readonly secondaryNavigationService = inject(SecondaryNavigationService);
  isTouchDevice = (navigator.maxTouchPoints ?? 0) > 0;

  setBookingTabValue = (event: BookingCategoryType) => {
    this.bookingTabService.setBookingTab(event);
  };

  closedFromButton = false;
  clickFromMobileNavButton = false;

  closeIconClicked(fromCloseButton = false): void {
    if (fromCloseButton) {
      this.clickFromMobileNavButton = false;
      const isShopHeader = !!document.querySelector('.header__shop');
      this.headerState.selectedNav.set(
        isShopHeader ? '' : this.headerState.selectedNav()
      );
    }
    this.closedFromButton = fromCloseButton;
    this.headerState.resetExpandPanel(this.closedFromButton);
    this.bodyScrollService.enableScroll();
    this.hideWidget();
  }

  hideWidget(): void {
    if (this.headerState.collapsedHeader && this.closedFromButton) {
      this.headerState.selectedNav.set('');
      this.headerState.previousPrimarySelectedNav = '';
    }
  }

  openMenu(): void {
    this.focusHamburgerIcon();
    this.toggleMenuState();
    this.bodyScrollService.disableScroll();
    this.headerState.modifyButtonClick.set(false);
    window.scrollTo(0, 0);
  }

  subscribeToFlyoutLang(): void {
    this.transSitesLangService.openFlyoutLang
      .pipe(
        filter(
          (
            data
          ): data is {
            openFlyoutConfirmationModal: boolean;
            countryCode: string;
            languageCode: string;
            secondaryLanguageCode: string;
          } => !!data?.openFlyoutConfirmationModal
        ),
        takeUntil(this.destroy$)
      )
      .subscribe((data) => {
        const content = this.headerFooterContent();
        this.flyoutLangSelectorLink =
          content?.header?.flyoutLanguageSelectorLinkLabel ||
          'Select other Location and Language';
        this.languageRedirectionFacade.setCLSData(
          data.countryCode,
          data.languageCode,
          data.secondaryLanguageCode,
          this.flyoutLangSelectorLink
        );
      });
  }

  openLoginModal(): void {
    this.loginModalService.openLoginModal();
  }

  resetLoginModalTrigger(): void {
    this.loginModalService.resetLoginModalTrigger();
  }

  secondaryNavAction(event: SecondaryNavActionEvent): void {
    this.secondaryNavigationService.handleSecondaryNavAction(event);
  }

  getNavValue(eventId: string, collapsedHeader: boolean): string {
    return this.secondaryNavigationService.getNavValue(
      eventId,
      this.headerState.previousPrimarySelectedNav,
      this.selectedPrimaryNav,
      this.darkSiteThemeService.isDarkSiteLevel(this.darkSiteLevel) ||
        collapsedHeader
    );
  }

  applyDarkThemeStyles(): void {
    this.darkSiteThemeService.applyDarkThemeStyles();
  }

  setMutationalObserver() {
    const observer = new MutationObserver(() => {
      if (this.darkSiteTheme === 'true') this.applyDarkThemeStyles();
    });
    observer.observe(this.elementRef.nativeElement, {
      childList: true,
      subtree: true
    });
  }

  private isClickOutsideNav(htmlElement: HTMLElement): boolean {
    return (
      !htmlElement?.closest('idp-secondary-navigation-links') &&
      !htmlElement?.closest('idp-navigation-links')
    );
  }

  closeSecondaryNavOnTouchOutside(htmlElement: HTMLElement): void {
    if (
      this.isTouchDevice &&
      this.headerState.selectedNav() ===
        this.headerState.navigationType.secondary &&
      this.isClickOutsideNav(htmlElement)
    ) {
      this.headerState.selectedNav.set('');
      this.headerState.secondaryNav = '';
    }
  }

  mouseout(): void {
    if (this.screenDetection.isDesktop()) {
      this.headerState.selectedNav.set(
        this.headerState.previousPrimarySelectedNav || ''
      );
      this.headerState.secondaryNav = '';
      this.cdr.detectChanges();
    }
  }
}
